import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, Sparkles, X, ArrowRight, Building2, FileText, ShoppingCart, Package, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_META = {
  company_profile: {
    title: "Complete your company profile",
    desc: "GSTIN, address & logo make invoices look professional. Recommended.",
    badge: "Recommended",
    icon: Building2,
    cta: "Open Setup",
    href: "/settings",
  },
  first_invoice: {
    title: "Create your first invoice",
    desc: "Build & send a GST-compliant invoice in under 60 seconds.",
    badge: "Recommended",
    icon: FileText,
    cta: "Create Invoice",
    href: "/create",
  },
  first_purchase_invoice: {
    title: "Upload a purchase invoice",
    desc: "Use OCR to auto-extract details from any bill (PDF / image).",
    badge: "Optional",
    icon: ShoppingCart,
    cta: "Upload Invoice",
    href: "/upload",
  },
  first_product: {
    title: "Add a product or service",
    desc: "Save items so they auto-fill on every new invoice.",
    badge: "Optional",
    icon: Package,
    cta: "Add Product",
    href: "/products",
  },
  ca_connected: {
    title: "Connect your CA",
    desc: "Let your accountant manage GST filings & document requests.",
    badge: "Optional",
    icon: UserCheck,
    cta: "Find CA",
    href: "/find-ca",
  },
};

const STEP_ORDER = ["company_profile", "first_product", "first_invoice", "first_purchase_invoice", "ca_connected"];

export default function OnboardingChecklist() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    fetchState();
  }, []);

  const fetchState = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/state`, { withCredentials: true });
      setState(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const dismiss = async () => {
    setDismissing(true);
    try {
      await axios.post(`${API}/onboarding/dismiss`, {}, { withCredentials: true });
      setState((s) => s && { ...s, dismissed: true });
    } catch { /* silent */ }
    finally { setDismissing(false); }
  };

  if (loading || !state) return null;
  if (state.dismissed) return null;
  if (state.all_complete) return null;

  const checks = state.checks || {};
  const total = STEP_ORDER.length;
  const done = STEP_ORDER.filter((k) => checks[k]).length;
  const pct = Math.round((done / total) * 100);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden mb-6"
        data-testid="onboarding-checklist"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 p-5 bg-gradient-to-r from-blue-50 via-violet-50 to-amber-50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Recommended setup actions
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                Complete any of these whenever it suits you · {done}/{total} done
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            title="Dismiss checklist"
            data-testid="onboarding-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-blue-500 via-violet-500 to-amber-400"
            data-testid="onboarding-progress"
          />
        </div>

        {/* Steps */}
        <div className="divide-y divide-slate-50">
          {STEP_ORDER.map((key) => {
            const meta = STEP_META[key];
            const isDone = !!checks[key];
            const Icon = meta.icon;
            return (
              <div
                key={key}
                className="flex items-center gap-3 p-4 transition-colors"
                data-testid={`onboarding-step-${key}`}
              >
                <div className="flex-shrink-0">
                  {isDone ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" data-testid={`onboarding-step-${key}-done`} />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-300" />
                  )}
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDone ? "bg-slate-50 text-slate-400" : "bg-blue-50 text-blue-600"}`}>
                  <Icon className="w-4 h-4" />
                </div>
              <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className={`text-sm font-semibold ${isDone ? "text-slate-400 line-through" : "text-slate-900"}`}>
                      {meta.title}
                    </p>
                    {!isDone && meta.badge && (
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${
                        meta.badge === "Recommended" ? "bg-blue-50 text-blue-700 border border-blue-100" : "bg-slate-100 text-slate-500"
                      }`}>
                        {meta.badge}
                      </span>
                    )}
                  </div>
                  {!isDone && <p className="text-xs text-slate-500 truncate">{meta.desc}</p>}
                </div>
                {!isDone && (
                  <Link
                    to={meta.href}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                    data-testid={`onboarding-cta-${key}`}
                  >
                    {meta.cta} <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
